/**
 * The stem tracks a separation would produce.
 *
 * One definition, used by both the Split Stems panel's download rows and the Mix
 * workspace's channel strips, because they are the same things — two lists would
 * eventually disagree about whether there is a "Guitar" or "Piano" stem.
 *
 * NOTHING HERE SEPARATES ANYTHING. These are the tracks the backend's Demucs split
 * returns, named so the UI can lay out a mixer over them and say honestly that they do not
 * exist yet.
 */

export interface StemTrack {
  id: string;
  label: string;
  /** What a separation model would put in this track. */
  description: string;
}

export const STEM_TRACKS: StemTrack[] = [
  { id: 'vocals', label: 'Vocals', description: 'Lead and backing voices.' },
  { id: 'bass', label: 'Bass', description: 'Bass guitar and low-end synth parts.' },
  { id: 'drums', label: 'Drums', description: 'Kick, snare, hats and percussion.' },
  { id: 'guitar', label: 'Guitar', description: 'Electric and acoustic guitar parts.' },
  { id: 'piano', label: 'Piano', description: 'Piano and keyboard-led harmonic parts.' },
  { id: 'other', label: 'Other', description: 'Anything the separation could not place elsewhere.' },
];

/**
 * The five fixed EQ bands the mixer exposes.
 *
 * Fixed bands, not a parametric EQ: the brief asks for five named ranges whose slider value
 * maps directly to gain in dB, and those labels/frequencies are what the backend renders.
 */
export type EqBandId = 'lowBass' | 'midBass' | 'mids' | 'highMids' | 'presence';

export interface EqBand {
  id: EqBandId;
  label: string;
  frequencyHz: number;
}

export type EqGains = Record<EqBandId, number>;

export const EQ_BANDS: EqBand[] = [
  { id: 'lowBass', label: 'Low Bass', frequencyHz: 60 },
  { id: 'midBass', label: 'Mid Bass', frequencyHz: 250 },
  { id: 'mids', label: 'Mids', frequencyHz: 1000 },
  { id: 'highMids', label: 'High Mids', frequencyHz: 4000 },
  { id: 'presence', label: 'Presence', frequencyHz: 16000 },
];

/** Flat EQ, keyed by band id. */
export function initialEqGains(): EqGains {
  return Object.fromEntries(EQ_BANDS.map((band) => [band.id, 0])) as EqGains;
}

export interface CompressorSettings {
  /** 0.1…100 ms. */
  attackMs: number;
  /** 50…1000 ms. */
  releaseMs: number;
  /** -60…0 dB. */
  thresholdDb: number;
  /** 1…20. */
  ratio: number;
  /** 0…24 dB. */
  makeupGainDb: number;
}

/** Neutral compressor settings: present, but effectively out of the way. */
export function initialCompressorSettings(): CompressorSettings {
  return {
    attackMs: 20,
    releaseMs: 200,
    thresholdDb: 0,
    ratio: 2,
    makeupGainDb: 0,
  };
}

export interface ReverbSettings {
  /** 10…250 ms. Longer means a bigger simulated room. */
  delayMs: number;
  /** 0…0.7. Higher means a longer tail before it fades out. */
  feedback: number;
  /** 0…1. How much of the original signal stays in front. */
  dryVolume: number;
  /** 0…0.5. How much reverb is added behind the dry signal. */
  wetVolume: number;
}

/** Neutral reverb: the controls are present, but the effect is effectively off. */
export function initialReverbSettings(): ReverbSettings {
  return {
    delayMs: 40,
    feedback: 0.2,
    dryVolume: 1,
    wetVolume: 0,
  };
}

export interface DelaySettings {
  /** 100…1000 ms. Longer means a slower, more obvious echo repeat. */
  delayMs: number;
  /** 0…0.7. Higher means more repeats before the echo dies out. */
  feedback: number;
  /** 0…1. Cross-fades the dry and delayed signal. */
  mix: number;
}

/** Neutral delay: the timing is ready, but the effect stays off until mix is raised. */
export function initialDelaySettings(): DelaySettings {
  return {
    delayMs: 350,
    feedback: 0.3,
    mix: 0,
  };
}

/**
 * The remaining effect-chain slots after EQ, compression, reverb and delay.
 *
 * Empty for now: everything the chain names has live controls above.
 */
export interface EffectSlot {
  id: string;
  label: string;
  /** Where it sits in the chain, and what it would be for. */
  description: string;
}

export const EFFECT_CHAIN: EffectSlot[] = [];

/** A channel's settings. Neutral values are the defaults a mixer opens at. */
export interface MixChannel {
  /** 0…1. 0.8 is unity in this scale, leaving headroom above it. */
  volume: number;
  /** −1 hard left … 1 hard right. */
  pan: number;
  muted: boolean;
  soloed: boolean;
}

export const NEUTRAL_CHANNEL: MixChannel = { volume: 0.8, pan: 0, muted: false, soloed: false };

/** A fresh channel per stem, keyed by stem id. */
export function initialChannels(): Record<string, MixChannel> {
  return Object.fromEntries(STEM_TRACKS.map((t) => [t.id, { ...NEUTRAL_CHANNEL }]));
}

/**
 * Whether a track would be heard, given the whole mixer's state.
 *
 * Solo is exclusive across the mixer, not a per-track flag: soloing the drums silences
 * everything else, which is what makes it useful and what a per-track `!muted` check gets
 * wrong. So this takes every channel rather than one.
 */
export function isAudible(id: string, channels: Record<string, MixChannel>): boolean {
  const channel = channels[id];
  if (!channel || channel.muted) return false;
  const anySolo = Object.values(channels).some((ch) => ch.soloed);
  return anySolo ? channel.soloed : true;
}

/** Pan position in words — "centre", "40% left", "hard right". */
export function describePan(pan: number): string {
  const amount = Math.round(Math.abs(pan) * 100);
  if (amount === 0) return 'Centre';
  const side = pan < 0 ? 'left' : 'right';
  if (amount === 100) return `Hard ${side}`;
  return `${amount}% ${side}`;
}
